import type React from "react";
import { Controller, useFormContext } from "react-hook-form";
import type { OnboardingFormData } from "../../../types/onboarding";
import FieldError from "../../FieldError";

/** Read-only summary field. */
const Field: React.FC<{
	label: string;
	value?: string | boolean | string[];
}> = ({ label, value }) => {
	let display: string;
	if (value === undefined || value === null || value === "") {
		display = "\u2014";
	} else if (typeof value === "boolean") {
		display = value ? "Yes" : "No";
	} else if (Array.isArray(value)) {
		display = value.length > 0 ? value.join(", ") : "\u2014";
	} else {
		display = String(value);
	}

	return (
		<div
			style={{
				display: "flex",
				justifyContent: "space-between",
				padding: "0.35rem 0",
				borderBottom: "1px solid var(--color-border)",
			}}
		>
			<span
				style={{ fontSize: "0.8rem", color: "var(--color-text-secondary)" }}
			>
				{label}
			</span>
			<span
				style={{
					fontSize: "0.8rem",
					color: "var(--color-text-primary)",
					fontWeight: 500,
					textAlign: "right",
					maxWidth: "60%",
				}}
			>
				{display}
			</span>
		</div>
	);
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
	title,
	children,
}) => (
	<div style={{ marginBottom: "1.25rem" }}>
		<h3
			style={{
				fontSize: "0.875rem",
				fontWeight: 600,
				color: "var(--color-text-primary)",
				marginBottom: "0.5rem",
				textTransform: "uppercase",
				letterSpacing: "0.05em",
			}}
		>
			{title}
		</h3>
		<div style={{ display: "flex", flexDirection: "column" }}>{children}</div>
	</div>
);

const ReviewStep: React.FC = () => {
	const {
		control,
		watch,
		formState: { errors },
	} = useFormContext<OnboardingFormData>();

	const data = watch();

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
			<h2
				style={{
					fontSize: "1.25rem",
					fontWeight: 600,
					color: "var(--color-text-primary)",
				}}
			>
				Review & Submit
			</h2>
			<p
				style={{
					fontSize: "0.875rem",
					color: "var(--color-text-secondary)",
					marginTop: "-0.5rem",
				}}
			>
				Please review your application before submitting.
			</p>

			{/* Company & Wallet */}
			<Section title="Company & Wallet">
				<Field label="Company Name" value={data.company_name} />
				<Field label="Wallet Name" value={data.wallet_name} />
				<Field label="Custodial Type" value={data.custodial_type} />
				<Field label="Chain" value={data.chain} />
				<Field label="Asset" value={data.asset} />
				<Field label="Products" value={data.products} />
				<Field label="Country" value={data.country_of_incorporation} />
				<Field label="Website" value={data.website} />
				<Field label="Description" value={data.business_description} />
			</Section>

			{/* Contact Details */}
			<Section title="Contact Details">
				<Field label="Name" value={data.primary_contact?.name} />
				<Field label="Email" value={data.primary_contact?.email} />
				<Field label="Phone" value={data.primary_contact?.phone} />
				<Field label="Role" value={data.primary_contact?.role} />
				{data.secondary_contact?.name && (
					<>
						<div
							style={{
								borderTop: "1px solid var(--color-border)",
								margin: "0.35rem 0",
							}}
						/>
						<Field
							label="Secondary Contact"
							value={data.secondary_contact.name}
						/>
						<Field
							label="Secondary Email"
							value={data.secondary_contact.email}
						/>
					</>
				)}
			</Section>

			{/* Terms */}
			<div
				style={{
					padding: "1rem",
					border: "1px solid var(--color-border)",
					borderRadius: "var(--border-radius)",
					background: "var(--color-bg-secondary)",
				}}
			>
				<Controller
					name="terms_accepted"
					control={control}
					render={({ field }) => (
						<label
							style={{
								display: "flex",
								alignItems: "flex-start",
								gap: "0.75rem",
								cursor: "pointer",
								fontSize: "0.875rem",
								color: "var(--color-text-primary)",
							}}
						>
							<input
								type="checkbox"
								checked={field.value || false}
								onChange={(e) => field.onChange(e.target.checked)}
								style={{
									marginTop: "0.15rem",
									accentColor: "var(--color-primary)",
									width: "1rem",
									height: "1rem",
								}}
							/>
							<span>
								I confirm that the information provided is accurate and
								complete. I agree to the{" "}
								<a
									href="https://www.moneygram.com/terms-and-conditions"
									target="_blank"
									rel="noopener noreferrer"
									style={{
										color: "var(--color-primary)",
										textDecoration: "underline",
									}}
								>
									Terms &amp; Conditions
								</a>{" "}
								and{" "}
								<a
									href="https://www.moneygram.com/privacy-notice"
									target="_blank"
									rel="noopener noreferrer"
									style={{
										color: "var(--color-primary)",
										textDecoration: "underline",
									}}
								>
									Privacy Notice
								</a>
								.
							</span>
						</label>
					)}
				/>
				<FieldError error={errors.terms_accepted} />
			</div>
		</div>
	);
};

export default ReviewStep;
